import { toCalendarDate, type MonthBar } from '@/components/calendar/month-grid'
import { CLEANING_TIER } from '@/components/calendar/cleaning-bars'
import { nextMonthStart } from './agenda'

/** A quiet band of the layers' own tier: pulsing softly, still for a viewer who asked for less motion. */
const BAND_CLASSES =
  'h-[22px] border-textFaint/30 bg-textFaint/20 text-transparent animate-pulse motion-reduce:animate-none'

export interface LoadingBands {
  /** Band 1: stays, blocks and gaps are on and not read yet. */
  stays: boolean
  /** Band 2: cleanings are on and not read yet. */
  cleaning: boolean
}

/**
 * Placeholder bars for a band whose layers are still loading: one strip across
 * every week of the month, in the band the real bars will sit in.
 *
 * An empty band reads as "nothing there", which is a claim; a pulsing strip
 * reads as "not here yet". The grid cuts a bar that spans the month into one
 * strip per week row, clipped to the month. They are decoration only - the
 * toolbar's live region says what is loading - so they are hidden from
 * assistive tech and carry no handler.
 */
export function loadingBands(month: string, { stays, cleaning }: LoadingBands): MonthBar[] {
  const start = toCalendarDate(`${month}-01`)
  const endExclusive = toCalendarDate(nextMonthStart(month))
  const band = (key: string, tier: number): MonthBar => ({
    key: `loading-${key}`,
    start,
    endExclusive,
    tier,
    label: null,
    className: BAND_CLASSES,
    testId: 'calendar-loading-band',
    data: { 'data-loading-band': key },
    decorative: true,
  })
  return [
    ...(stays ? [band('stays', 0)] : []),
    ...(cleaning ? [band('cleaning', CLEANING_TIER)] : []),
  ]
}
